import React, { useState, useEffect } from "react";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultBadge from "../../../../components/badge/defaultBadge";
import { HiCurrencyRupee, HiClock, HiChevronRight } from "react-icons/hi";
import moment from "moment";
import SalaryAssignWizard from "./SalaryAssignWizard";
import SalaryHistoryModal from "./SalaryHistoryModal";
import { getCurrentSalary } from "../../../../../services/workspace/salaryStructure.service";
import toast from "react-hot-toast";

const SalaryCard = ({ employeeId, employeeData, onUpdate }) => {
  const [showWizard, setShowWizard] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [upcomingChange, setUpcomingChange] = useState(null);
  const [loadingSalary, setLoadingSalary] = useState(false);

  // Fetch current salary from API
  useEffect(() => {
    const fetchSalaryData = async () => {
      // Use employeeId or employeeData.uuid
      const userId = employeeId || employeeData?.id || employeeData?.id;
      if (!userId) return;

      setLoadingSalary(true);
      try {
        const currentSalaryData = await getCurrentSalary(userId);
        if (currentSalaryData) {
          // Transform API response to match component format
          setCurrentSalary({
            id: currentSalaryData.id || currentSalaryData.uuid,
            structureName: currentSalaryData.structure_name || currentSalaryData.structureName || currentSalaryData.salary_structure?.name || "N/A",
            effectiveDate: currentSalaryData.effective_date || currentSalaryData.effectiveDate,
            status: currentSalaryData.status || "active",
            grossPay: currentSalaryData.gross_pay || currentSalaryData.grossPay || 0,
            netPay: currentSalaryData.net_pay || currentSalaryData.netPay || 0,
            ctc: currentSalaryData.annual_ctc || currentSalaryData.ctc || currentSalaryData.annualCTC || 0,
          });

          // Check for upcoming change
          if (currentSalaryData.upcoming_change) {
            setUpcomingChange({
              structureName: currentSalaryData.upcoming_change.structure_name || currentSalaryData.upcoming_change.structureName,
              effectiveDate: currentSalaryData.upcoming_change.effective_date || currentSalaryData.upcoming_change.effectiveDate,
              ctc: currentSalaryData.upcoming_change.ctc || currentSalaryData.upcoming_change.annual_ctc,
            });
          } else {
            setUpcomingChange(null);
          }
        } else {
          setCurrentSalary(null);
          setUpcomingChange(null);
        }
      } catch (error) {
        console.error("Failed to fetch salary data:", error);
        // Don't show error toast if it's just 404 (no salary assigned yet)
        if (error?.response?.status !== 404) {
          toast.error("Failed to fetch salary information");
        }
        setCurrentSalary(null);
        setUpcomingChange(null);
      } finally {
        setLoadingSalary(false);
      }
    };

    fetchSalaryData();
  }, [employeeId, employeeData?.uuid, employeeData?.id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD MMM YYYY");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <DefaultBadge status={1} custom="Active" />;
      case "upcoming":
        return <DefaultBadge status={0} custom="Upcoming" />;
      case "expired":
        return <DefaultBadge status={2} custom="Expired" />;
      default:
        return <DefaultBadge status={0} custom="Pending" />;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-borderColor shadow-sm">
      {/* Card Header */}
      <div className="border-b border-borderColor p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-theme">Salary Information</h3>
          <div className="flex gap-2">
            <DefaultButton
              variant="secondary"
              name="View History"
              onClick={() => setShowHistory(true)}
              classnames="text-sm"
            />
            <DefaultButton
              variant="default"
              name={currentSalary ? "Edit Salary Structure" : "Assign Salary Structure"}
              onClick={() => setShowWizard(true)}
              classnames="text-sm"
            />
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        {loadingSalary ? (
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-primary-100 rounded"></div>
            <div className="h-32 bg-primary-100 rounded"></div>
          </div>
        ) : currentSalary ? (
          <div className="flex flex-col gap-4">
            {/* Current Assignment */}
            <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <HiCurrencyRupee className="text-theme text-xl" />
                  <span className="text-sm font-medium text-theme">Current Assignment</span>
                </div>
                {getStatusBadge(currentSalary.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-textLight mb-1">Structure</p>
                  <p className="text-sm font-semibold text-theme">{currentSalary.structureName}</p>
                </div>
                <div>
                  <p className="text-xs text-textLight mb-1">Effective Date</p>
                  <div className="flex items-center gap-1">
                    <HiClock className="text-textLight text-xs" />
                    <p className="text-sm font-semibold text-theme">
                      {formatDate(currentSalary.effectiveDate)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-borderColor rounded-lg p-3">
                <p className="text-xs text-textLight mb-1">Gross Pay</p>
                <p className="text-base font-semibold text-theme">
                  {formatCurrency(currentSalary.grossPay)}
                </p>
                <p className="text-xs text-textLight mt-1">Monthly</p>
              </div>
              <div className="bg-white border border-borderColor rounded-lg p-3">
                <p className="text-xs text-textLight mb-1">Net Pay</p>
                <p className="text-base font-semibold text-success-600">
                  {formatCurrency(currentSalary.netPay)}
                </p>
                <p className="text-xs text-textLight mt-1">Monthly</p>
              </div>
              <div className="bg-white border border-borderColor rounded-lg p-3">
                <p className="text-xs text-textLight mb-1">Annual CTC</p>
                <p className="text-base font-semibold text-theme">
                  {formatCurrency(currentSalary.ctc)}
                </p>
                <p className="text-xs text-textLight mt-1">Per Year</p>
              </div>
              <div className="bg-white border border-borderColor rounded-lg p-3">
                <p className="text-xs text-textLight mb-1">Deductions</p>
                <p className="text-base font-semibold text-error-600">
                  {formatCurrency(currentSalary.grossPay - currentSalary.netPay)}
                </p>
                <p className="text-xs text-textLight mt-1">Monthly</p>
              </div>
            </div>

            {/* Upcoming Change */}
            {upcomingChange && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HiClock className="text-warning-600" />
                    <div>
                      <p className="text-sm font-medium text-warning-900">Upcoming Change</p>
                      <p className="text-xs text-warning-700">
                        New structure "{upcomingChange.structureName}" effective from{" "}
                        {formatDate(upcomingChange.effectiveDate)}
                      </p>
                    </div>
                  </div>
                  <HiChevronRight className="text-warning-600" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <HiCurrencyRupee className="text-4xl text-textLight mx-auto mb-3" />
            <p className="text-textLight mb-4">No salary structure assigned</p>
            <DefaultButton
              variant="default"
              name="Assign Salary Structure"
              onClick={() => setShowWizard(true)}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showWizard && (
        <SalaryAssignWizard
          employeeId={employeeId}
          employeeData={employeeData}
          currentSalary={currentSalary}
          onClose={() => setShowWizard(false)}
          onSave={async (newSalary) => {
            // Refresh salary data after save
            const userId = employeeId || employeeData?.uuid || employeeData?.id;
            if (userId) {
              try {
                const currentSalaryData = await getCurrentSalary(userId);
                if (currentSalaryData) {
                  setCurrentSalary({
                    id: currentSalaryData.id || currentSalaryData.uuid,
                    structureName: currentSalaryData.structure_name || currentSalaryData.structureName || currentSalaryData.salary_structure?.name || "N/A",
                    effectiveDate: currentSalaryData.effective_date || currentSalaryData.effectiveDate,
                    status: currentSalaryData.status || "active",
                    grossPay: currentSalaryData.gross_pay || currentSalaryData.grossPay || 0,
                    netPay: currentSalaryData.net_pay || currentSalaryData.netPay || 0,
                    ctc: currentSalaryData.annual_ctc || currentSalaryData.ctc || currentSalaryData.annualCTC || 0,
                  });

                  // Check for upcoming change
                  if (currentSalaryData.upcoming_change) {
                    setUpcomingChange({
                      structureName: currentSalaryData.upcoming_change.structure_name || currentSalaryData.upcoming_change.structureName,
                      effectiveDate: currentSalaryData.upcoming_change.effective_date || currentSalaryData.upcoming_change.effectiveDate,
                      ctc: currentSalaryData.upcoming_change.ctc || currentSalaryData.upcoming_change.annual_ctc,
                    });
                  } else {
                    setUpcomingChange(null);
                  }
                }
              } catch (error) {
                console.error("Failed to refresh salary data:", error);
              }
            }
            
            onUpdate?.(newSalary);
            setShowWizard(false);
          }}
        />
      )}

      {showHistory && (
        <SalaryHistoryModal
          employeeId={employeeId}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

export default SalaryCard;

